import React, { useState } from "react";
import { useIntl } from "react-intl";

import { ModalComponent } from "@app/core/modal";
import { DeleteModal } from "@app/modules/delete-modal/delete-modal.component";
import {
  DeleteManyExternalDealFiltersResponseDTO,
  GetExternalDealFiltersSortOrderEnum,
  OutExternalDealFilterDTO,
  OutMerchantDTO,
  PlatformDTO
} from "@app/api/generated";
import { LabelComponent } from "@app/core";
import { useAppDispatch, useAppSelector } from "@app/redux/store";
import { Column, ISortDirection } from "@webbio/components";
import { Table } from "@app/components/table/table";
import { ROUTES } from "@app/constants/routes";
import { useDateFnsFormat } from "@app/util/use-date-fns-format";
import { platformTranslationKeys } from "@app/constants/platform";
import { externalDealFiltersThunk } from "@app/redux/thunks/external-deal-filters.thunk";
import { externalDealFiltersActions } from "@app/redux/reducers/external-deal-filters";
import { CheckboxComponent } from "@app/core/checkbox";
import { Overview } from "@app/components/overview/overview";
import { TableActions } from "@app/core/table-actions";
import { useOnButtonLinkClick } from "@app/util/use-on-button-link-click";
import { useFilterState } from "@app/util/use-filter-state";

import { ExternalDealFilterFilterBar } from "./external-deal-filter-filter-bar/external-deal-filter-filter-bar.component";
import { EdfEditBulkModal } from "../external-deal-filter-modal";
import { EdfDuplicateBulkModal } from "../external-deal-filter-duplicate-modal";
import { ExternalDealFilterOptionsBar } from "./options-bar/external-deal-filter-options-bar-component";

export interface SelectedFilter {
  id: number;
  platformId: number;
}

const ExternalDealFilterOverview = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {
    externalDealFilters,
    isLoading,
    currentPage,
    currentPlatform,
    currentMerchantFilter,
    currentSearch,
    currentStatus,
    currentEan,
    currentLiveDeals,
    currentIdSortOrder,
    take,
    totalPages,
    totalResults
  } = useAppSelector((state) => state.externalDealFilters);

  const { onButtonLinkClick } = useOnButtonLinkClick();
  const [isDeleteFilterOpen, setIsDeleteFilterOpen] = useState<boolean>(false);
  const [isEditBulkOpen, setIsEditBulkOpen] = useState<boolean>(false);
  const [isDuplicateBulkOpen, setIsDuplicateBulkOpen] = useState<boolean>(false);
  const [isDeleteBulkOpen, setIsDeleteBulkOpen] = useState<boolean>(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState<boolean>(false);
  const [filterToDelete, setFilterToDelete] = useState<OutExternalDealFilterDTO | undefined>(undefined);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);

  const { formatDate, getTimeDifference, formatDistance } = useDateFnsFormat();

  useFilterState({
    deps: {
      currentSearch,
      currentStatus,
      currentPlatform,
      currentMerchantFilter,
      currentEan,
      currentLiveDeals,
      currentIdSortOrder,
      take
    },
    onFirstChange: () => getExternalDealFilters(currentPage),
    onChange: () => getExternalDealFilters(1)
  });

  const onDeleteExternalDealFilter = (externalDealFilter: OutExternalDealFilterDTO) => {
    setFilterToDelete(externalDealFilter);
    setIsDeleteFilterOpen(true);
  };

  const onDeleteConfirmed = () => {
    if (filterToDelete) {
      dispatch(externalDealFiltersThunk.deleteExternalDealFilter(filterToDelete.id));
      setIsDeleteFilterOpen(false);
    }
  };

  const onDeleteBulkConfirmed = async () => {
    setIsDeletingBulk(true);
    const result: DeleteManyExternalDealFiltersResponseDTO | undefined = (await dispatch(
      externalDealFiltersThunk.deleteManyExternalDealFilters({
        filterIds: selectedFilters.map((filter) => ({ id: filter.id }))
      })
    )) as any;
    setIsDeletingBulk(false);
    setIsDeleteBulkOpen(false);

    if (result) {
      setSelectedFilters([]);
      getExternalDealFilters(1);
    }
  };

  const onSort = (columnId?: string, sortDirection?: ISortDirection) => {
    if (columnId !== "id") {
      return;
    }
    dispatch(
      externalDealFiltersActions.setCurrentIdSortOrder(
        sortDirection === "ASC"
          ? GetExternalDealFiltersSortOrderEnum.ASC
          : sortDirection === "DESC"
          ? GetExternalDealFiltersSortOrderEnum.DESC
          : undefined
      )
    );
  };

  const onPageChange = (page: number) => {
    getExternalDealFilters(page);
  };

  const getExternalDealFilters = (newPage?: number) => {
    const page = newPage || currentPage;
    const skip = (page - 1) * take;

    dispatch(
      externalDealFiltersThunk.getExternalDealFilters(
        skip,
        take,
        currentMerchantFilter,
        currentPlatform,
        currentSearch,
        currentStatus,
        { ean: currentEan, liveDeals: currentLiveDeals, idSortOrder: currentIdSortOrder }
      )
    );
    dispatch(externalDealFiltersActions.setCurrentPage(page));

    window.scrollTo(0, 0);
  };

  const onTableRowAuxClick = (row: OutExternalDealFilterDTO, e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) =>
    onTableRowClick(row, e, true);

  const onTableRowClick = (
    row: OutExternalDealFilterDTO,
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    forceTargetBlank?: boolean
  ) => {
    const route = `${ROUTES.EXTERNAL_DEAL_FILTERS}/${row.id}`;

    onButtonLinkClick(route, e, forceTargetBlank);
  };

  const onSelectRow = (changeEvent: React.ChangeEvent<HTMLInputElement>, filterId: number, platformId: number) => {
    const newSelectedFilters = [...selectedFilters];
    if (changeEvent.target.checked) {
      newSelectedFilters.push({ id: filterId, platformId });
    } else {
      const indexToDelete = newSelectedFilters.findIndex((filter) => filter.id === filterId);
      newSelectedFilters.splice(indexToDelete, 1);
    }
    setSelectedFilters(newSelectedFilters);
  };

  const onSelectAll = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFilters(
      changeEvent.target.checked
        ? addFiltersToList([...selectedFilters], externalDealFilters ?? [])
        : removeFiltersFromList([...selectedFilters], externalDealFilters ?? [])
    );
  };

  // Derived from the visible rows, so it stays right when the page size, sorting or filters change.
  const isCurrentPageSelected =
    !!externalDealFilters &&
    externalDealFilters.length > 0 &&
    externalDealFilters.every((edf) => selectedFilters.some((filter) => filter.id === edf.id));

  return (
    <Overview
      title={intl.formatMessage(
        { id: "externalDealFilters.overview.title" },
        { total: intl.formatNumber(totalResults) }
      )}
      actionBar={<ExternalDealFilterFilterBar />}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      overviewItems={externalDealFilters}
      header={
        <ExternalDealFilterOptionsBar
          selectedFilters={selectedFilters}
          onEditStatus={() => getExternalDealFilters()}
          onDuplicate={() => setIsDuplicateBulkOpen(true)}
          onEdit={() => setIsEditBulkOpen(true)}
          onDelete={() => setIsDeleteBulkOpen(true)}
        />
      }
    >
      {externalDealFilters && externalDealFilters.length > 0 && (
        <Table
          dataSource={externalDealFilters || []}
          isLoading={isLoading}
          onSort={onSort}
          defaultSort={{ column: "id", direction: currentIdSortOrder }}
        >
          <Column
            title={
              <CheckboxComponent
                name=""
                onChange={(changeEvent: React.ChangeEvent<HTMLInputElement>) => {
                  onSelectAll(changeEvent);
                }}
                value={isCurrentPageSelected}
                noMargin
              />
            }
            id="selectedFilters"
            field="id"
            isNotSortable
            cell={(_, __, edf: OutExternalDealFilterDTO) => {
              return (
                <CheckboxComponent
                  name=""
                  onChange={(changeEvent: React.ChangeEvent<HTMLInputElement>) => {
                    onSelectRow(changeEvent, edf.id, edf.platform.id);
                  }}
                  value={selectedFilters.some((filter) => filter.id === edf.id)}
                  noMargin
                />
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.id" })}
            field="id"
            id="id"
            sortField="id"
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(id) => id}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.status" })}
            field="live"
            id="live"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            maxWidth={80}
            width={80}
            cell={(live) => <LabelComponent type={live ? "live" : "offline"} />}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.title" })}
            field="title"
            id="title"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(title) => (
              <span className="truncate-table" title={title}>
                {title || "-"}
              </span>
            )}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.lastRunStartDate" })}
            field="lastRunStartDate"
            id="lastRunStartDate"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(lastRunStartDate: string) => {
              const date = lastRunStartDate ? formatDate(new Date(lastRunStartDate), "dd-MM-yy HH:mm:ss") : "-";

              return (
                <span className="truncate-table" title={date}>
                  {date}
                </span>
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.lastRunDuration" })}
            field="id"
            id="lastRunDuration"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, edf: OutExternalDealFilterDTO) => {
              const lastRunStartDate = edf?.lastRunStartDate && new Date(edf?.lastRunStartDate);
              const lastRunEndDate = edf?.lastRunEndDate && new Date(edf?.lastRunEndDate);

              if (!lastRunStartDate || !lastRunEndDate) {
                return <>-</>;
              }

              const isInProgress = Boolean(
                edf?.lastRunStartDate && edf?.lastRunEndDate && edf.lastRunStartDate > edf?.lastRunEndDate
              );

              if (isInProgress) {
                return (
                  <span className="truncate-table">
                    {intl.formatMessage({
                      id: "externalDealFilters.detail.form.info.runningProgress.placeholder"
                    })}
                  </span>
                );
              }

              const timeDifference = formatDistance(lastRunEndDate, lastRunStartDate);
              const exactTimeDifference = getTimeDifference(lastRunEndDate, lastRunStartDate);

              return (
                <span className="truncate-table" title={exactTimeDifference}>
                  {timeDifference}
                </span>
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.nextRunAtDate" })}
            field="nextRunAtDate"
            id="nextRunAtDate"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(nextRunAtDate, __, edf: OutExternalDealFilterDTO) => {
              const date = nextRunAtDate
                ? formatDate(new Date(nextRunAtDate), "dd-MM-yy HH:mm:ss")
                : intl.formatMessage({ id: "externalDealFilters.overview.table.td.nextRunAtDate.default" });

              const isInProgress = Boolean(
                edf?.lastRunStartDate && edf?.lastRunEndDate && edf.lastRunStartDate > edf?.lastRunEndDate
              );

              const label = isInProgress ? "-" : date;

              return (
                <span className="truncate-table" title={label}>
                  {label}
                </span>
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.runIntervalSeconds" })}
            field="runIntervalSeconds"
            id="runIntervalSeconds"
            isNotSortable
            maxWidth={80}
            width={80}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(runIntervalSeconds: number) => <>{runIntervalSeconds}s</>}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.searchQuery" })}
            field="searchQuery"
            id="searchQuery"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(searchQuery: string) => (
              <span className="truncate-table" title={searchQuery}>
                {searchQuery}
              </span>
            )}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.merchants" })}
            field="merchants"
            id="merchants"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(merchants?: OutMerchantDTO[]) => {
              const combinedMerchants = merchants?.map((merchant) => merchant.name).join(", ");

              return (
                <span className="truncate-table" title={combinedMerchants}>
                  {combinedMerchants}
                </span>
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "externalDealFilters.overview.table.th.platform" })}
            field="platform"
            id="platform"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(platform?: PlatformDTO) => {
              const translationKey = platformTranslationKeys.find((platformItem) => platformItem.id === platform?.id);
              const platformString = translationKey ? intl.formatMessage({ id: translationKey.name }) : "-";

              return (
                <span className="truncate-table" title={platformString}>
                  {platformString}
                </span>
              );
            }}
          />
          <Column
            title=""
            field="id"
            id="actions"
            isNotSortable
            cell={(_, __, edf: OutExternalDealFilterDTO) => {
              return (
                <TableActions
                  editLink={`${ROUTES.EXTERNAL_DEAL_FILTERS}/${edf.id}`}
                  onDelete={() => onDeleteExternalDealFilter(edf)}
                />
              );
            }}
          />
        </Table>
      )}

      <ModalComponent
        title={intl.formatMessage({ id: "externalDealFilters.overview.modal.delete.title" })}
        isModalOpen={isDeleteFilterOpen}
        onCloseModal={() => setIsDeleteFilterOpen(false)}
        variant="big"
      >
        <DeleteModal onCancel={() => setIsDeleteFilterOpen(false)} onDelete={onDeleteConfirmed} />
      </ModalComponent>

      <ModalComponent
        title={intl.formatMessage(
          { id: "externalDealFilters.overview.modal.deleteBulk.title" },
          { amount: selectedFilters.length }
        )}
        isModalOpen={isDeleteBulkOpen}
        onCloseModal={() => setIsDeleteBulkOpen(false)}
        variant="big"
      >
        {isDeleteBulkOpen ? (
          <DeleteModal
            onCancel={() => setIsDeleteBulkOpen(false)}
            onDelete={onDeleteBulkConfirmed}
            isLoading={isDeletingBulk}
            description={intl.formatMessage(
              { id: "externalDealFilters.overview.modal.deleteBulk.description" },
              { amount: selectedFilters.length }
            )}
          />
        ) : (
          <></>
        )}
      </ModalComponent>

      <ModalComponent
        title={intl.formatMessage(
          { id: "externalDealFilters.overview.modal.edit.title" },
          { amount: selectedFilters.length }
        )}
        isModalOpen={isEditBulkOpen}
        onCloseModal={() => {
          setIsEditBulkOpen(false);
        }}
        variant="big"
      >
        <EdfEditBulkModal
          onCancel={() => {
            setIsEditBulkOpen(false);
          }}
          onDone={() => {
            getExternalDealFilters();
            setIsEditBulkOpen(false);
          }}
          selectedFilters={selectedFilters}
        />
      </ModalComponent>

      <ModalComponent
        title={intl.formatMessage({ id: "externalDealFilters.modal.duplicate.title" })}
        isModalOpen={isDuplicateBulkOpen}
        onCloseModal={() => {
          setIsDuplicateBulkOpen(false);
        }}
        variant="big"
      >
        <EdfDuplicateBulkModal
          onCancel={() => {
            setIsDuplicateBulkOpen(false);
          }}
          onDone={() => {
            getExternalDealFilters();
            setIsDuplicateBulkOpen(false);
          }}
          selectedFilters={selectedFilters}
        />
      </ModalComponent>
    </Overview>
  );
};

const addFiltersToList = (currentFilters: SelectedFilter[], filtersToAdd: OutExternalDealFilterDTO[]) => {
  const convertedFiltersToAdd: SelectedFilter[] =
    filtersToAdd.map((filter) => {
      return { id: filter.id, platformId: filter.platform.id };
    }) ?? [];
  const currentIds = new Set(currentFilters.map((filter) => filter.id));

  return [...currentFilters, ...convertedFiltersToAdd.filter((filter) => !currentIds.has(filter.id))];
};

const removeFiltersFromList = (currentFilters: SelectedFilter[], filtersToRemove: OutExternalDealFilterDTO[]) => {
  const filtersToRemoveIds = new Set(filtersToRemove.map((filter) => filter.id));

  return currentFilters.filter((filter) => !filtersToRemoveIds.has(filter.id));
};

export { ExternalDealFilterOverview };
